/**
 * lex.ivoliq Kanzlei-Portal (Juli 2026, Security-Stufen Aug 2026) — /kanzlei
 * Internes Cockpit der lex.ivoliq GmbH (Haus-StB) — bewusst AUSSERHALB von /advisor/*.
 * Login-Stufen: Passwort → (Erstlogin: Passwortwechsel) → (2FA-Code) → Cockpit.
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAdvisorAuth } from '@/contexts/AdvisorAuthContext';
import AdvisorLexCockpit from '@/pages/AdvisorLexCockpit';
import '@/styles/lex.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KanzleiPortal() {
  const { advisor, loading, login } = useAdvisorAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const setupToken = searchParams.get('setup');
  const [step, setStep] = useState(setupToken ? 'setup' : 'login'); // login | pwchange | mfa | setup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [setupInfo, setSetupInfo] = useState(null); // {email,name} | 'invalid'
  const [setupError, setSetupError] = useState('');

  useEffect(() => {
    if (!setupToken || advisor) return;
    axios.get(`${API}/advisor/auth/setup-info`, { params: { token: setupToken } })
      .then((res) => setSetupInfo(res.data))
      .catch((err) => {
        setSetupInfo('invalid');
        const detail = err?.response?.data?.detail;
        setSetupError(typeof detail === 'string' ? detail : 'Der Einrichtungs-Link ist ungültig.');
      });
  }, [setupToken, advisor]);

  const finishLogin = async () => {
    const me = await axios.get(`${API}/advisor/auth/me`);
    // Stufen-State zurücksetzen, damit nach Logout wieder die Login-Maske erscheint
    setStep('login'); setPendingToken(''); setPassword(''); setNewPassword(''); setNewPassword2(''); setMfaCode('');
    login(me.data);
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (newPassword !== newPassword2) { toast.error('Die Passwörter stimmen nicht überein.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/advisor/auth/setup-password`, { token: setupToken, new_password: newPassword });
      setSearchParams({}, { replace: true });
      toast.success('Zugang eingerichtet. Willkommen!');
      await finishLogin();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Einrichtung fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/advisor/auth/login`, { email, password, portal: 'kanzlei' });
      const status = res.data?.status;
      if (status === 'password_change_required') {
        setPendingToken(res.data.pending_token);
        setStep('pwchange');
      } else if (status === 'mfa_required') {
        setPendingToken(res.data.pending_token);
        setStep('mfa');
      } else {
        await finishLogin();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Anmeldung fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePwChange = async (e) => {
    e.preventDefault();
    if (newPassword !== newPassword2) { toast.error('Die Passwörter stimmen nicht überein.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/advisor/auth/force-change-password`, {
        pending_token: pendingToken, current_password: password, new_password: newPassword,
      });
      if (res.data?.status === 'mfa_required') {
        setPendingToken(res.data.pending_token);
        setStep('mfa');
      } else {
        toast.success('Passwort gesetzt. Willkommen!');
        await finishLogin();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Passwortwechsel fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/advisor/auth/2fa/verify`, { pending_token: pendingToken, code: mfaCode });
      await finishLogin();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Code ungültig.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="lex-theme min-h-screen flex items-center justify-center" style={{ background: '#FDFCFB' }}>
        <div className="animate-spin h-10 w-10 border-b-2" style={{ borderColor: '#0A1128' }} />
      </div>
    );
  }

  if (advisor) return <AdvisorLexCockpit />;

  return (
    <div className="lex-theme min-h-screen flex items-center justify-center px-6" style={{ background: '#0A1128' }} data-testid="kanzlei-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-10 lex-fade-up">
          <span className="lex-serif text-3xl font-semibold text-white">lex<span style={{ color: '#C5A059' }}>.</span>ivoliq</span>
          <p className="text-xs uppercase tracking-[0.3em] font-semibold mt-3" style={{ color: '#C5A059' }}>Kanzlei-Portal</p>
          <p className="text-sm text-slate-400 mt-2">Interner Zugang der lex.ivoliq Steuerberatung.</p>
        </div>

        {step === 'setup' && (
          <div className="bg-white p-8 space-y-4 lex-fade-up-1" data-testid="kanzlei-setup-form">
            {setupInfo === 'invalid' ? (
              <>
                <p className="text-sm text-slate-600" data-testid="kanzlei-setup-invalid">{setupError}</p>
                <button type="button" className="text-xs text-slate-400 underline w-full"
                  onClick={() => { setSearchParams({}, { replace: true }); setStep('login'); }}
                  data-testid="kanzlei-setup-back">
                  Zur Anmeldung
                </button>
              </>
            ) : !setupInfo ? (
              <p className="text-sm text-slate-500 text-center">Link wird geprüft …</p>
            ) : (
              <form onSubmit={handleSetup} className="space-y-4">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold" style={{ color: '#0A1128' }}>Zugang einrichten:</span>{' '}
                  Willkommen{setupInfo.name ? `, ${setupInfo.name}` : ''}! Bitte legen Sie das Passwort
                  für <strong>{setupInfo.email}</strong> fest (mind. 12 Zeichen).
                </p>
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Passwort</label>
                  <input type="password" className="lex-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    minLength={12} required autoFocus data-testid="kanzlei-setup-password-input" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Passwort wiederholen</label>
                  <input type="password" className="lex-input" value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)}
                    minLength={12} required data-testid="kanzlei-setup-password2-input" />
                </div>
                <button type="submit" className="lex-btn w-full" disabled={submitting} data-testid="kanzlei-setup-submit">
                  {submitting ? 'Bitte warten …' : 'Passwort festlegen & anmelden'}
                </button>
              </form>
            )}
          </div>
        )}

        {step === 'login' && (
          <form onSubmit={handleLogin} className="bg-white p-8 space-y-4 lex-fade-up-1">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">E-Mail</label>
              <input type="email" className="lex-input" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="kanzlei@ivoliq.io" required data-testid="kanzlei-email-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Passwort</label>
              <input type="password" className="lex-input" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="kanzlei-password-input" />
            </div>
            <button type="submit" className="lex-btn w-full" disabled={submitting} data-testid="kanzlei-submit-btn">
              {submitting ? 'Bitte warten …' : 'Anmelden'}
            </button>
          </form>
        )}

        {step === 'pwchange' && (
          <form onSubmit={handlePwChange} className="bg-white p-8 space-y-4 lex-fade-up-1" data-testid="kanzlei-pwchange-form">
            <p className="text-sm text-slate-600">
              <span className="font-semibold" style={{ color: '#0A1128' }}>Erstanmeldung:</span>{' '}
              Bitte vergeben Sie jetzt ein eigenes Passwort (mind. 12 Zeichen).
            </p>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Neues Passwort</label>
              <input type="password" className="lex-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                minLength={12} required data-testid="kanzlei-newpassword-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Neues Passwort wiederholen</label>
              <input type="password" className="lex-input" value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)}
                minLength={12} required data-testid="kanzlei-newpassword2-input" />
            </div>
            <button type="submit" className="lex-btn w-full" disabled={submitting} data-testid="kanzlei-pwchange-submit">
              {submitting ? 'Bitte warten …' : 'Passwort setzen & anmelden'}
            </button>
          </form>
        )}

        {step === 'mfa' && (
          <form onSubmit={handleMfa} className="bg-white p-8 space-y-4 lex-fade-up-1" data-testid="kanzlei-mfa-form">
            <p className="text-sm text-slate-600">
              <span className="font-semibold" style={{ color: '#0A1128' }}>Zwei-Faktor-Bestätigung:</span>{' '}
              Geben Sie den 6-stelligen Code Ihrer Authenticator-App oder einen Recovery-Code ein.
            </p>
            <input type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus className="lex-input text-center tracking-[0.4em] text-lg"
              value={mfaCode} onChange={(e) => setMfaCode(e.target.value.trim())} placeholder="123456" required data-testid="kanzlei-mfa-code-input" />
            <button type="submit" className="lex-btn w-full" disabled={submitting} data-testid="kanzlei-mfa-submit">
              {submitting ? 'Bitte warten …' : 'Bestätigen'}
            </button>
            <button type="button" className="text-xs text-slate-400 underline w-full" onClick={() => { setStep('login'); setMfaCode(''); }} data-testid="kanzlei-mfa-back">
              Zurück zur Anmeldung
            </button>
          </form>
        )}

        <p className="lex-mono text-xs text-slate-500 text-center mt-8">ivoliq GmbH · ATU83347308</p>
      </div>
    </div>
  );
}
